<script setup>
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useContactMergeI18n } from '../useContactMergeI18n';

const props = defineProps({
  contact: { type: Object, required: true },
  summary: { type: Object, default: null },
  role: { type: String, default: '' },
});
const { t } = useContactMergeI18n();
const owner = computed(
  () => props.contact.portfolio_owner || props.contact.portfolioOwner
);
const fields = computed(() => [
  { key: 'EMAIL', value: props.contact.email },
  { key: 'OWNER', value: owner.value?.name || t('MERGE_DIALOG.NO_OWNER') },
  { key: 'TEAM', value: props.contact.team?.name },
  {
    key: 'COMPANY',
    value:
      (
        props.contact.additional_attributes ||
        props.contact.additionalAttributes
      )?.company_name || props.contact.additionalAttributes?.companyName,
  },
]);
</script>

<template>
  <section
    class="min-w-0 rounded-[16px] p-[16px]"
    :class="
      role === 'PRIMARY'
        ? 'border-2 border-[#10a344] bg-white'
        : role
          ? 'border border-[#edf0f3] bg-[#fafafa]'
          : 'bg-[#f6f7f7]'
    "
  >
    <span
      v-if="role"
      class="mb-[16px] inline-flex rounded-full px-[10px] py-[4px] text-[11px] font-bold leading-none"
      :class="
        role === 'PRIMARY'
          ? 'bg-[#10a344] text-white'
          : 'bg-[#fff3f2] text-[#dc2626]'
      "
    >
      {{ t(`MERGE_DIALOG.${role}`) }}
    </span>
    <div class="flex min-w-0 items-center gap-[12px]">
      <Avatar
        :name="contact.name || ''"
        :src="contact.thumbnail || ''"
        :size="44"
      />
      <div class="min-w-0 flex-1">
        <p
          class="m-0 truncate text-[14px] leading-[20px] font-semibold text-[#111827]"
        >
          {{ contact.name }}
        </p>
        <p class="m-0 truncate text-[12px] leading-[16px] text-[#667085]">
          {{
            contact.phone_number ||
            contact.phoneNumber ||
            contact.email ||
            contact.identifier
          }}
        </p>
        <p
          v-if="summary"
          class="m-0 mt-[2px] text-[11px] leading-[14px] text-[#8a95a5]"
        >
          {{
            t('MERGE_DIALOG.CONNECTIONS', summary.connections, {
              count: summary.connections,
            })
          }}
          ·
          {{
            t('MERGE_DIALOG.CONVERSATIONS', summary.conversations, {
              count: summary.conversations,
            })
          }}
          <template v-if="!role && owner?.name">
            · {{ t('MERGE_DIALOG.OWNER') }}: {{ owner.name }}
          </template>
        </p>
      </div>
    </div>
    <dl v-if="role" class="m-0 mt-[16px] text-[12px] leading-[16px]">
      <div
        v-for="field in fields"
        :key="field.key"
        class="flex items-start justify-between gap-[12px] border-t border-[#f0f2f5] py-[8px]"
      >
        <dt class="shrink-0 text-[#667085]">
          {{ t(`MERGE_DIALOG.${field.key}`) }}
        </dt>
        <dd class="m-0 min-w-0 break-words text-right text-[#111827]">
          {{ field.value || '—' }}
        </dd>
      </div>
    </dl>
  </section>
</template>
